/**
 * OnboardingDisclaimerModal — Phase B regulatory onboarding gate.
 *
 * Shown to non-owner-tier users on first login (and again if the disclaimer
 * version is bumped). Backend decides via /api/disclaimers/status; this
 * component only renders when `needs_onboarding_modal=true`.
 *
 * On accept → POST /api/disclaimers/accept → reload status (modal disappears).
 *
 * Owner tier (Simon, Unman) never sees the modal — handled server-side
 * in `needs_onboarding_modal()`.
 */
import { useCallback, useEffect, useState } from 'react'

import {
  acceptDisclaimer,
  getDisclaimerCopy,
  getDisclaimerStatus,
} from '@/lib/api'
import type { DisclaimerCopy, DisclaimerStatus } from '@/lib/types'

export default function OnboardingDisclaimerModal() {
  const [status, setStatus] = useState<DisclaimerStatus | null>(null)
  const [copy, setCopy] = useState<DisclaimerCopy | null>(null)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // One-shot fetch on mount. The status response includes an explicit
  // `needs_onboarding_modal` boolean that handles tier policy server-side
  // — we render whatever the server says.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [s, c] = await Promise.all([
          getDisclaimerStatus(),
          getDisclaimerCopy(),
        ])
        if (!cancelled) {
          setStatus(s)
          setCopy(c)
        }
      } catch {
        // 401 (unauthenticated) → don't render. Other errors fail open.
        if (!cancelled) setStatus(null)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const handleAccept = useCallback(async () => {
    setAccepting(true)
    setError(null)
    try {
      await acceptDisclaimer({
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        client_locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
      })
      // Re-poll status; modal hides when has_accepted_current=true.
      const next = await getDisclaimerStatus()
      setStatus(next)
    } catch (e) {
      setError(String(e))
    } finally {
      setAccepting(false)
    }
  }, [])

  if (!status?.needs_onboarding_modal || !copy) {
    return null
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="bg-surface-DEFAULT border border-border rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
        <h2
          id="onboarding-title"
          className="text-lg font-semibold text-text-primary"
        >
          {copy.onboarding.title}
        </h2>
        <div className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
          {copy.onboarding.body}
        </div>
        <div className="text-[11px] text-text-muted leading-relaxed border-t border-border pt-3">
          {copy.scenario}
        </div>
        {error && (
          <p className="text-xs text-rose-light bg-rose/10 border border-rose/30 rounded px-3 py-2">
            Couldn&apos;t record acceptance: {error}
          </p>
        )}
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-medium hover:bg-blue-light transition-colors disabled:opacity-60"
          >
            {accepting ? 'Saving…' : copy.onboarding.cta}
          </button>
        </div>
      </div>
    </div>
  )
}
